<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
    <cf-geolocation-input
      v-if="showSearchInput"
      ref="geolocationInput"
      :is-inline="inlineSearchResults"
      :loading="searchResults.areLoading"
      @geolocated="search"
    />

    <div
      v-else-if="searchResults.areLoading"
      class="cf-section-loader-container"
    >
      <div class="cf-loader" />
    </div>

    <div
      v-if="!searchResults.areLoading && searchResults.locations.length > 0"
      class="cf-flex cf-flex--column cf-flex--gap-12"
    >
      <h3 class="cf-title cf-title--3 cf-text-align-left">
        Select a Location
      </h3>

      <ul>
        <li
          v-for="location in searchResults.locations"
          :key="location.storeId"
          class="cf-flex cf-flex--column cf-flex--gap-8 cf-list-item-padding-bottom cf-list-item-margin-bottom cf-list-item-border-bottom"
        >
          <div class="cf-grid cf-grid--2-grid-column-left-large cf-grid--gap-16 cf-mobile-column">
            <!-- Left Side Grid Column -->
            <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-text-align-left">
              <!-- Store Name and Status -->
              <div class="cf-flex cf-flex--column cf-flex--gap-6">
                <h2 class="cf-title cf-title--2">
                  {{ location.description }}
                </h2>

                <p
                  :class="['cf-subtitle cf-subtitle--2 cf-subtitle--heavy', {
                    'cf-subtitle--green': location.currentDayOperatingSchedule.status === 'Online',
                    'cf-subtitle--red': location.currentDayOperatingSchedule.status !== 'Online'
                  }]"
                >
                  {{ getStoreOperationStatus(location) }}
                </p>
              </div>

              <!-- Store Address Details -->
              <div class="cf-flex cf-flex--column cf-flex--gap-6">
                <cf-store-directions
                  :address="location.address"
                  :element-type="'link'"
                  :display-icon="false"
                  show-landmark-or-cross-street
                />

                <p
                  v-if="!showingAllLocations"
                  class="cf-subtitle cf-subtitle--2"
                >
                  {{ $miles(location.distance) }}
                </p>
              </div>

              <!-- Store Hours and Phone -->
              <div class="cf-flex cf-flex--column cf-flex--gap-6">
                <cf-store-operating-hours
                  v-if="location.hasStoreHours"
                  :location="location"
                />

                <cf-store-phone-number
                  v-if="location.phoneNumber"
                  :phone-number="location.phoneNumber"
                  :element-type="'link'"
                />
              </div>

              <!-- Store Details button (Page View Location Detail)-->
              <button
                v-if="showStoreInfo"
                title="View store details"
                type="button"
                class="button button--small button--icon button--light auto-spacing-right"
                @click="$openModal('locationDetails', { location, hideOrderingModes: true })"
              >
                <span class="icon fa-info dark-gray" />
              </button>
            </div>

            <!-- Right Side Grid Column -->
            <div
              v-if="selectable"
              class="cf-flex cf-flex--align-center cf-flex--justify-end"
            >
              <!-- Store Select Button -->
              <button
                v-if="showSelected && selectedStoreId === location.storeId && !isSelecting"
                type="button"
                class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
              >
                <span class="cf-icon fa-check gray" />
                Selected
              </button>

              <button
                v-else
                :class="[
                  'cf-button cf-button--lg cf-button--fill',
                  {
                    'cf-button--loading': location.storeId === locationIdBeingSelected && isSelecting,
                    'cf-button--primary': !showStoreValidations || location.currentDayOperatingSchedule.status === 'Online',
                    'cf-button--secondary': !showStoreValidations || location.currentDayOperatingSchedule.status !== 'Online',
                  }
                ]"
                type="button"
                :disabled="showStoreValidations && !location.searchedAttributes.supported"
                @click="select(location)"
              >
                {{ getLocationSelectionText(location) }}
              </button>
            </div>
          </div>

          <!-- Store Validations -->
          <div
            v-if="showStoreValidations && location.searchedAttributes.messages.length > 0"
            class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-10 cf-alt-background"
          >
            <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

            <ul class="cf-flex cf-flex--column cf-text-align-left">
              <li
                v-for="(message, index) in location.searchedAttributes.messages"
                :key="index"
                class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy"
              >
                {{ message }}
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>

    <button
      v-if="!searchResults.areLoading && searchResults.moreResultsExist || searchResults.moreResultsAreLoading"
      :class="['cf-button cf-button--tertiary cf-button--lg cf-button--fill', { 'cf-button--loading': searchResults.moreResultsAreLoading }]"
      type="button"
      @click="showMoreResults"
    >
      Show More
    </button>

    <transition name="fade-down-only-in">
      <p
        v-if="hasSearched && !searchResults.areLoading && searchResults.locations.length === 0"
        class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy"
      >
        Sorry, no locations were found within {{ searchDistance }} miles.
      </p>
    </transition>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfGeolocationInput from '@/view/components/shared/cf-geolocation-input/index.vue';
  import CfStoreDirections from '@/view/components/shared/cf-store-directions/index.vue';
  import CfStorePhoneNumber from '@/view/components/shared/cf-store-phone-number/index.vue';
  import CfStoreOperatingHours from '@/view/components/shared/cf-store-operating-hours/index.vue';


  export default {
    name: 'CfLocationSelector',

    components: {
      CfGeolocationInput,
      CfStoreDirections,
      CfStorePhoneNumber,
      CfStoreOperatingHours
    },

    props: {
      isSelecting: {
        type: Boolean,
        default: false
      },

      inlineSearchResults: {
        type: Boolean,
        default: false
      },

      listClass: {
        type: String,
        default: null
      },

      selectable: {
        type: Boolean,
        default: true
      },

      showStoreInfo: {
        type: Boolean,
        default: false
      },

      showAllStores: {
        type: Boolean,
        default: false
      },

      /**
       * This prop is used to force the search input to be shown,
       * even if the merchant config is set to show all stores.
       */
      showSearch: {
        type: Boolean,
        default: false
      },

      showSelected: {
        type: Boolean,
        default: true
      },

      selectedStoreId: {
        type: Number,
        default: null
      },
      /**
       * Since this component is also used to select a favorite store,
       * we want to hide the validations regardless of the stores operating hours.
      */
      showStoreValidations: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        hasSearched: false,
        locationIdBeingSelected: null,
        searchDistance: null,
        showingAllLocations: true
      };
    },

    computed: {
      ...mapGetters('locations', ['searchResults']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),

      showSearchInput() {
        return this.showSearch || !this.showingAllLocations;
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.$store.dispatch('locations/resetSearchLocations');

        this.loadAllStores();

        this.searchDistance = this.merchant.options.searchRadiusInMiles || 50;
      },

      async loadAllStores() {
        /**
         * merchant config to indicate that a user should only be able to select
         * from all possible stores instead of being able to search for a store
         * or use their location to view their closest stores
         */
        const { showAllStores: showAllStoresConfig } = this.merchant.options;
        /**
         * component prop to indicate that a user should
         * be able to select from all possible stores
         */
        const { showAllStores: showAllStoresProp } = this;

        this.showingAllLocations = showAllStoresConfig || showAllStoresProp;

        if (this.showingAllLocations) {
          /**
          * this is a "hack" to allow the searchable area
          * to be the entire continental united states
          */
          await this.$store.dispatch('locations/searchLocations', {
            distance: 3000,
            latitude: 39.50,
            longitude: -98.35
          });
        }

        else {
          this.$store.dispatch('locations/resetSearchLocations');
        }
      },

      async search(coordinates) {
        if (!coordinates) {
          this.hasSearched = false;
          this.loadAllStores();
        }
        else {
          await this.$store.dispatch('locations/searchLocations', {
            distance: this.searchDistance,
            latitude: coordinates.latitude,
            longitude: coordinates.longitude
          });

          this.hasSearched = true;
        }
      },

      select(location) {
        this.locationIdBeingSelected = location.storeId;

        this.$emit('select', location);

        // this.$triggerGoogleTrackingEvent('SelectLocation', 'Locations');
      },

      async showMoreResults() {
        await this.$store.dispatch('locations/showMoreSearchLocations');
      },

      getLocationSelectionText(location) {
        const selectionTextMap = {
          IMMEDIATE_ORDERING: 'Select',
          FUTURE_SAME_DAY_ORDERING: 'Order for Later Today',
          FUTURE_NEXT_DAY_ORDERING: 'Order for Later',
          UNAVAILABLE_ORDERING: 'View Menu'
        };

        return this.showStoreValidations
          ? selectionTextMap[location.locationOrderingStatus]
          : selectionTextMap.IMMEDIATE_ORDERING;
      },

      getStoreOperationStatus(location) {
        return location.currentDayOperatingSchedule.status === 'Online'
          ? 'Open Now'
          : 'Closed';
      }
    }
  };
</script>
